import React, { useState, useEffect } from 'react';
import SideMenu from "../../component/SideMenu";
import Topbar from "../../component/Topbar";
import axios from 'axios';
import Swal from 'sweetalert2';
import { baseurl } from '../../baseurl';
import { MessageSquare, Send, X, Clock, CheckCircle, AlertCircle } from 'lucide-react';

const Ticket = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);

  // Modal States
  const [showModal, setShowModal] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [adminReply, setAdminReply] = useState('');
  const [statusUpdate, setStatusUpdate] = useState('');

  const adminToken = localStorage.getItem('adminToken');

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseurl}getAllTicketsForAdmin`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      if (response.data.success) setTickets(response.data.tickets);
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to fetch support tickets' });
    } finally {
      setTimeout(() => setLoading(false), 800);
    }
  };

  useEffect(() => { fetchTickets(); }, [adminToken]);

  // Handle Modal Open
  const handleOpenModal = (ticket) => {
    setSelectedTicket(ticket);
    setAdminReply(ticket.admin_response || '');
    setStatusUpdate(ticket.status);
    setShowModal(true);
  };

  // Handle Submit Update
  const handleSubmitUpdate = async () => {
    try {
      const response = await axios.post(`${baseurl}updateTicketAdmin`, {
        ticketId: selectedTicket.id,
        admin_response: adminReply,
        status: statusUpdate
      }, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });

      if (response.data.success) {
        Swal.fire('Updated!', 'Ticket has been updated successfully.', 'success');
        setShowModal(false);
        fetchTickets();
      }
    } catch (err) {
      Swal.fire('Error', 'Update failed.', 'error');
    }
  };

  // Logic: Sorting, Filtering, Pagination
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
    setSortConfig({ key, direction });
  };

  const filteredTickets = tickets
    .filter(t => `${t.subject} ${t.full_name} ${t.id}`.toLowerCase().includes(searchTerm.toLowerCase()))
    .sort((a, b) => {
      if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'asc' ? -1 : 1;
      if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

  const currentRows = filteredTickets.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);

  return (
    <div className="app-container">
      <Topbar />
      <div className="main-wrapper" style={{ background: '#F4F7FE', display: 'flex' }}>
       
        <div className="profile-content-container UD-page-wrapper p-4" style={{ flex: 1, overflow: 'auto' }}>
          
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h2 className="fw-bold mb-1" style={{ color: '#1B2559' }}>Support Center</h2>
              <p className="text-muted m-0">Manage and resolve user tickets.</p>
            </div>
            <span className="badge rounded-pill bg-white text-primary px-3 py-2 shadow-sm border">
              {filteredTickets.length} Total Tickets
            </span>
          </div>

          {/* TABLE */}
          <div className="card border-0 shadow-sm overflow-hidden" style={{ borderRadius: '15px' }}>
            <div className="table-responsive">
              <table className="table table-hover align-middle mb-0">
                <thead className="bg-light">
                  <tr>
                    <th className="ps-4">ID</th>
                    <th>SUBJECT</th>
                    <th>USER</th>
                    <th className="text-center">STATUS</th>
                    <th>DATE</th>
                    <th className="text-center">ACTION</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    [1,2,3].map(i => <tr key={i}><td colSpan="6" className="p-4 text-center">Loading...</td></tr>)
                  ) : currentRows.map((ticket) => (
                    <tr key={ticket.id}>
                      <td className="ps-4 text-muted">#TKT-{ticket.id}</td>
                      <td>
                        <div className="fw-bold text-dark">{ticket.subject}</div>
                        <div className="small text-muted text-truncate" style={{maxWidth: '200px'}}>{ticket.description}</div>
                      </td>
                      <td>
                        <div className="small fw-bold">{ticket.full_name}</div>
                        <div className="small text-muted">{ticket.email}</div>
                      </td>
                      <td className="text-center">
                        <span className={`status-pill ${ticket.status.toLowerCase().replace(' ', '-')}`}>
                          {ticket.status}
                        </span>
                      </td>
                      <td className="text-muted small">{new Date(ticket.created_at).toLocaleDateString()}</td>
                      <td className="text-center">
                        <button onClick={() => handleOpenModal(ticket)} className="btn btn-primary btn-sm rounded-pill px-3 d-inline-flex align-items-center gap-2">
                          <MessageSquare size={14} /> Reply
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* CUSTOM MODAL */}
      {showModal && (
        <div className="modal-backdrop-custom">
          <div className="modal-card shadow-lg">
            <div className="modal-header-custom">
              <h5 className="m-0 fw-bold">Ticket Details</h5>
              <button className="btn-close-custom" onClick={() => setShowModal(false)}><X size={20}/></button>
            </div>
            
            <div className="modal-body-custom">
              <div className="ticket-info-box mb-3">
                <label className="text-primary small fw-bold uppercase">User Query:</label>
                <p className="mb-0 text-dark">{selectedTicket?.description}</p>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <label className="form-label small fw-bold">Status Update</label>
                  <select className="form-select" value={statusUpdate} onChange={(e) => setStatusUpdate(e.target.value)}>
                    <option value="Open">Open</option>
                    <option value="Pending">Pending</option>
                    <option value="Resolved">Resolved</option>
                    <option value="Closed">Closed</option>
                  </select>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label small fw-bold">Admin Note / Response</label>
                <textarea 
                  className="form-control" 
                  rows="4" 
                  placeholder="Type your response here..."
                  value={adminReply}
                  onChange={(e) => setAdminReply(e.target.value)}
                ></textarea>
              </div>
            </div>

            <div className="modal-footer-custom">
              <button className="btn btn-light rounded-pill px-4" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn btn-primary rounded-pill px-4" onClick={handleSubmitUpdate}>
                <Send size={16} className="me-2"/> Save Update
              </button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
        .status-pill { padding: 4px 12px; border-radius: 20px; font-size: 11px; font-weight: 700; text-transform: uppercase; }
        .status-pill.open { background: #E8F5E9; color: #2E7D32; }
        .status-pill.pending { background: #FFF3E0; color: #EF6C00; }
        .status-pill.resolved { background: #E3F2FD; color: #1565C0; }

        .modal-backdrop-custom {
          position: fixed; top: 0; left: 0; width: 100%; height: 100%;
          background: rgba(0, 0, 0, 0.4); backdrop-filter: blur(4px);
          display: flex; align-items: center; justify-content: center; z-index: 9999;
        }
        .modal-card {
          background: white; width: 550px; border-radius: 20px;
          overflow: hidden; animation: slideUp 0.3s ease-out;
        }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        
        .modal-header-custom { padding: 20px; border-bottom: 1px solid #eee; display: flex; justify-content: space-between; align-items: center; }
        .modal-body-custom { padding: 20px; }
        .modal-footer-custom { padding: 15px 20px; background: #f8f9fa; display: flex; justify-content: flex-end; gap: 10px; }
        .ticket-info-box { background: #f0f4ff; padding: 15px; border-radius: 12px; border-left: 4px solid #4318FF; }
        .btn-close-custom { background: none; border: none; color: #aaa; cursor: pointer; }
        .btn-close-custom:hover { color: #000; }
        `}
      </style>
    </div>
  );
};

export default Ticket;